import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { Plus, Pencil, Trash2, Eye, Printer, Search, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGlobalBarcodeScanner } from '@/hooks/useGlobalBarcodeScanner';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CATEGORIES = ['TOP', 'BOTTOM SKIRT', 'BOTTOM PANT', 'CO-ORD', 'JUMPSUIT', 'DRESS'];

export default function ArticleData({ user }) {
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete } = usePermissions(user);
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [editArticle, setEditArticle] = useState(null);
  const [viewDetails, setViewDetails] = useState(null);
  const [printBarcode, setPrintBarcode] = useState(null);
  const [printSize, setPrintSize] = useState({ width: 50, height: 30 });
  const [formData, setFormData] = useState({ ln_x_code: 'LN-X-', article_category: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [deleteColorDialogOpen, setDeleteColorDialogOpen] = useState(false);
  const [colorToDelete, setColorToDelete] = useState(null);

  // Global barcode scanner for Articles  
  const handleGlobalBarcodeScanned = (scannedCode) => {
    const found = articles.find(article => article.ln_x_code.toLowerCase() === scannedCode.toLowerCase());
    if (found) {
      setViewDetails(found);
      toast.success(`✅ Article found: ${found.ln_x_code}`);
    } else {
      toast.error(`❌ No article found with barcode: ${scannedCode}`);
    }
  };

  useGlobalBarcodeScanner(handleGlobalBarcodeScanned);

  useEffect(() => { fetchArticles(); }, []);

  useEffect(() => {
    let filtered = articles;

    // Filter by LN-X Code (exact and partial matching for barcode scanning)
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(article => {
        // Exact match first (for barcode scanning)
        if (article.ln_x_code.toLowerCase() === query) {
          return true;
        }
        // Partial match (for regular typing)
        return article.ln_x_code.toLowerCase().includes(query);
      });
    }

    // Filter by Article Category
    if (categoryFilter !== '') {
      filtered = filtered.filter(article => 
        article.article_category === categoryFilter
      );
    }

    setFilteredArticles(filtered);
  }, [searchQuery, categoryFilter, articles]);

  const extractNumeric = (code) => {
    return code.replace(/\D/g, '').replace(/^0+/, '');
  };

  const handleBarcodeSearch = (e) => {
    if (e && e.key === 'Enter' && searchQuery.trim() !== '') {
      e.preventDefault();
      const query = searchQuery.toLowerCase().trim();
      
      console.log('🔍 Searching articles for:', query);
      
      // Find article by exact or partial match
      const found = articles.find(article => {
        return article.ln_x_code.toLowerCase() === query || 
               article.ln_x_code.toLowerCase().includes(query);
      });
      
      if (found) {
        console.log('✅ Found article:', found.ln_x_code);
        setViewDetails(found);
        toast.success(`✅ Found: ${found.ln_x_code}`);
      } else {
        console.log('❌ No article found for:', query);
        toast.error(`❌ No article found: ${query}`);
      }
    }
  };

  const fetchArticles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/articles-master`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(response.data);
      setFilteredArticles(response.data);
    } catch (error) {
      toast.error('Failed to fetch articles');
    }
  };

  // Auto-refresh every 5 seconds, but pause when user is editing
  const isUserEditing = open || !!editArticle;
  useAutoRefresh(fetchArticles, 5, isUserEditing);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // Use custom_category if OTHER is selected
      const dataToSend = {
        ...formData,
        article_category: formData.article_category === 'OTHER' 
          ? formData.custom_category || 'OTHER' 
          : formData.article_category
      };
      
      // Remove custom_category field before sending
      delete dataToSend.custom_category;
      
      if (editArticle) {
        await axios.put(`${API}/articles-master/${editArticle.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/articles-master`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Created successfully');
      }
      setOpen(false);
      setEditArticle(null);
      setFormData({ ln_x_code: 'LN-X-', article_category: '' });
      fetchArticles();
    } catch (error) {
      console.error('Article save error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Operation failed';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (article) => {
    setArticleToDelete(article);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!articleToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/articles-master/${articleToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted successfully');
      setDeleteDialogOpen(false);
      setArticleToDelete(null);
      fetchArticles();
    } catch (error) {
      toast.error('Delete failed');
      setDeleteDialogOpen(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setArticleToDelete(null);
  };

  const handleRemoveColor = (index) => {
    setColorToDelete(index);
    setDeleteColorDialogOpen(true);
  };

  const confirmColorDelete = () => {
    if (colorToDelete === null) return;
    const updatedColors = formData.colors.filter((_, i) => i !== colorToDelete);
    setFormData({ ...formData, colors: updatedColors });
    setDeleteColorDialogOpen(false);
    setColorToDelete(null);
    toast.success('Color removed');
  };

  const cancelColorDelete = () => {
    setDeleteColorDialogOpen(false);
    setColorToDelete(null);
  };

    const handlePrint = () => {
    if (!printBarcode) return;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Barcode - ${printBarcode.code}</title>
          <style>
            @page { 
              size: ${printSize.width}mm ${printSize.height}mm;
              margin: 0;
            }
            @media print {
              body { margin: 0; padding: 0; }
            }
            body { 
              margin: 0; 
              padding: 0; 
              display: flex; 
              justify-content: center; 
              align-items: center; 
              width: ${printSize.width}mm;
              height: ${printSize.height}mm;
            }
            .barcode-container { 
              text-align: center;
              page-break-inside: avoid;
              width: 100%;
              height: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            img { 
              max-width: 100%;
              max-height: 100%;
              display: block;
            }
          </style>
        </head>
        <body>
          <div class="barcode-container">
            <img src="${printBarcode.image}" alt="Barcode" onload="window.print(); window.onafterprint = function() { window.close(); }" />
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Article (LN-X-CODE)</h1>
              <p className="text-sm text-gray-600">Manage article codes</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6 space-y-4">
        {/* Main Search Bar */}
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search or scan barcode" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} 
              onKeyDown={handleBarcodeSearch} 
              className="pl-10"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              tabIndex="1"
              data-barcode-input="true"
            />
          </div>
          {canCreate('master_data') && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => { 
                  setEditArticle(null); 
                  setFormData({ ln_x_code: 'LN-X-', article_category: '' });
                }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Article
                </Button>
              </DialogTrigger>
            </Dialog>
          )}
        </div>

        {/* Filters Section */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Article Category</Label>
            <SearchableSelect
              options={[
                { value: "", label: "All Categories" },
                ...CATEGORIES.map(category => ({ value: category, label: category }))
              ]}
              value={categoryFilter}
              onChange={(value) => setCategoryFilter(value)}
              placeholder="All Categories"
              searchPlaceholder="Search category..."
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Actions</Label>
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchQuery('');
                setCategoryFilter('');
              }}
              className="w-full text-sm"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) setErrorMessage(''); }}>
        <DialogContent>
            <DialogHeader><DialogTitle>{editArticle ? 'Edit Article' : 'Add New Article (LN-X-CODE)'}</DialogTitle></DialogHeader>
            {errorMessage && (
              <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-bold text-red-800 mb-1">Error</h3>
                    <p className="text-sm text-red-700">{errorMessage}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setErrorMessage('')}
                    className="ml-auto flex-shrink-0 inline-flex text-red-400 hover:text-red-600"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label><span className="text-red-500">*</span> LN-X Code</Label>
                <Input 
                  value={formData.ln_x_code} 
                  onChange={(e) => {
                    setFormData({ ...formData, ln_x_code: e.target.value });
                  }}
                  required 
                  placeholder="LN-X-" 
                />
                <p className="text-xs text-gray-500">Starts with LN-X- prefix, complete the rest manually</p>
              </div>
              <div className="space-y-2">
                <Label><span className="text-red-500">*</span> Article Category</Label>
                <SearchableSelect
                  options={[
                    { value: "", label: "Select Article Category" },
                    { value: "TOP", label: "TOP" },
                    { value: "BOTTOM SKIRT", label: "BOTTOM SKIRT" },
                    { value: "BOTTOM PANT", label: "BOTTOM PANT" },
                    { value: "CO-ORD", label: "CO-ORD" },
                    { value: "DRESS", label: "DRESS" },
                    { value: "ABAYA", label: "ABAYA" },
                    { value: "HIJAB", label: "HIJAB" },
                    { value: "OTHER", label: "OTHER" }
                  ]}
                  value={formData.article_category}
                  onChange={(value) => setFormData({ ...formData, article_category: value })}
                  placeholder="Select Category"
                  searchPlaceholder="Search category..."
                  required
                />
                {formData.article_category === 'OTHER' && (
                  <Input
                    type="text"
                    placeholder="Enter custom category"
                    value={formData.custom_category || ''}
                    onChange={(e) => setFormData({ ...formData, custom_category: e.target.value })}
                    className="mt-2"
                  />
                )}
              </div>
              <Button type="submit" className="w-full">{editArticle ? 'Update' : 'Create'} Article</Button>
            </form>
          </DialogContent>
        </Dialog>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {filteredArticles.map((article) => (
          <div key={article.id} className="bg-white border-2 border-black rounded-lg shadow p-4 flex flex-col items-center justify-between hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setViewDetails(article)}>
            <h3 className="text-lg font-bold text-center mb-2">{article.ln_x_code}</h3>
            <p className="text-xs text-gray-500 text-center mb-3">{article.article_category}</p>
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              {canEdit('master_data') && (
                <Button variant="ghost" size="icon" onClick={() => { 
                  setEditArticle(article);
                  const predefinedCategories = ['TOP', 'BOTTOM SKIRT', 'BOTTOM PANT', 'CO-ORD', 'DRESS', 'ABAYA', 'HIJAB'];
                  if (predefinedCategories.includes(article.article_category)) {
                    setFormData(article);
                  } else {
                    setFormData({ ...article, article_category: 'OTHER', custom_category: article.article_category });
                  }
                  setOpen(true);
                }}>
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              {canDelete('master_data') && (
                <Button variant="ghost" size="icon" onClick={() => handleDelete(article)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredArticles.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No articles found.</p>
        </div>
      )}

      <Dialog open={!!viewDetails} onOpenChange={() => setViewDetails(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Article Details - {viewDetails?.ln_x_code}</DialogTitle></DialogHeader>
          {viewDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="font-semibold">LN-X Code:</span> {viewDetails.ln_x_code}</div>
                <div><span className="font-semibold">Category:</span> {viewDetails.article_category}</div>
                <div><span className="font-semibold">Barcode:</span> {viewDetails.barcode?.code}</div>
              </div>
              {viewDetails.barcode_path && (
                <div className="border-t pt-4 space-y-3">
                  <Button variant="outline" size="sm" className="w-full" onClick={() => setPrintBarcode({ 
                    image: `${API}/${viewDetails.barcode_path}`, 
                    code: viewDetails.ln_x_code 
                  })}>
                    <Printer className="w-3 h-3 mr-1" /> Print Barcode
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!printBarcode} onOpenChange={() => setPrintBarcode(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Print Barcode - {printBarcode?.code}</DialogTitle></DialogHeader>
          {printBarcode && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img src={printBarcode.image} alt="Barcode" className="max-w-full" />
              </div>
              <div className="space-y-3">
                <Label>Print Size (mm)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Width</Label>
                    <Input type="number" value={printSize.width} onChange={(e) => setPrintSize({ ...printSize, width: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-sm">Height</Label>
                    <Input type="number" value={printSize.height} onChange={(e) => setPrintSize({ ...printSize, height: e.target.value })} />
                  </div>
                </div>
              </div>
              <Button onClick={handlePrint} className="w-full">
                <Printer className="w-4 h-4 mr-2" /> Print Barcode
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {/* Delete Article Authorization Dialog */}
      <AuthorizationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onAuthorized={confirmDelete}
        title="Delete Article"
        message={articleToDelete ? `Enter authorization code to delete article ${articleToDelete.ln_x_code}` : 'Enter authorization code to proceed'}
      />

      {/* Delete Color Authorization Dialog */}
      <AuthorizationDialog
        open={deleteColorDialogOpen}
        onOpenChange={setDeleteColorDialogOpen}
        onAuthorized={confirmColorDelete}
        title="Remove Color"
        message={colorToDelete !== null && formData.colors[colorToDelete] ? `Enter authorization code to remove color ${formData.colors[colorToDelete].color_name}` : 'Enter authorization code to proceed'}
      />
      </main>
    </div>
  );
}